import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@csa/web-foundation";
import type {
  BookFilter,
  BookHousehold,
  BookSummaryView,
  Consent,
  CreateLifeEventInput,
  Engagement,
  HouseholdCommission,
  Interaction,
  LifeEvent,
  TimelineEntry,
  LogInteractionInput,
  Member,
  Opportunity,
  OwnershipView,
  ParticipationInput,
  Policy,
  ReassignOwnerInput,
  RenewalScheduleItem,
} from "./types";

const hh = (id: string, panel: string) => ["household", id, panel];

/** A party-search hit (U1). `householdId` is present only when the find result carries it
 *  — U1-B's name-find returns it ("ad + household id + il"); the existing phone/tckn find
 *  (PartySummary) does NOT, so a row without it can't yet open the 360 (flagged). Kept
 *  tolerant so U1-F survives either find shape and lights up when the backend supplies it. */
export interface PartyHit {
  id: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  householdId?: string | null;
}

/** GET /v1/parties/find?{phone|name}= — scope=own party search (the API enforces scope +
 *  RLS). phone → {parties}; name (U1-B, paired) → {parties}. Tolerant + graceful: a 404
 *  (incl. name-find not-yet-deployed) or an absent list → empty, never a crash. No retry —
 *  a miss is a state, not a transient. TCKN is never a search mode here (never shown). */
export function usePartySearch(mode: "phone" | "name", query: string) {
  const api = useApi();
  const q = query.trim();
  return useQuery({
    queryKey: ["party-search", mode, q],
    enabled: q.length > 0,
    retry: false,
    queryFn: async () => {
      const res = await api.get<{ parties?: PartyHit[] }>(
        `/v1/parties/find?${mode}=${encodeURIComponent(q)}`,
      );
      return res?.parties ?? [];
    },
  });
}

// usePartyList (GET /v1/parties) was DELETED — dead since Müşterilerim moved to the
// advisor book read (useBookSummary, #202/#207). Repo-wide it had exactly one
// reference: its own definition. Kept out rather than kept "just in case": a
// plausible-looking unused hook is how the next person ships a second party list.

export function useOwnership(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "ownership"),
    queryFn: () => api.get<OwnershipView>(`/v1/households/${id}/ownership`),
  });
}
export function useMembers(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "members"),
    queryFn: () => api.get<Member[]>(`/v1/households/${id}/members`),
  });
}
export function usePolicies(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "policies"),
    queryFn: () => api.get<Policy[]>(`/v1/households/${id}/policies`),
  });
}
export function useInteractions(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "interactions"),
    queryFn: () => api.get<Interaction[]>(`/v1/households/${id}/interactions`),
  });
}
export function useLifeEvents(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "life-events"),
    queryFn: () => api.get<LifeEvent[]>(`/v1/households/${id}/life-events`),
  });
}
/** GET …/timeline — the server-composed relationship history (#260), newest-first,
 *  masked (enums + line codes only). No browser-side aggregation. */
export function useHouseholdTimeline(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "timeline"),
    queryFn: () => api.get<TimelineEntry[]>(`/v1/households/${id}/timeline`),
  });
}
/** Per-member consent state (the consent panel aggregates these). */
/** POST /v1/consents/:id/withdraw — GAP-AUDIT-3 D18. The endpoint exists precisely
 *  for this ("staff may record a data subject's request", consent:write@all, audited)
 *  and the console had no control, so a customer saying "stop marketing to me" could
 *  not be honoured from the surface where the advisor is standing.
 *
 *  Withdraw only — NOT grant. POST /v1/parties/:id/consents is party-principals-only
 *  by design (the customer grants their own açık rıza; the advisor path is the
 *  interaction's CAPTURED_IN_INTERACTION attestation, where lawful basis is derived
 *  server-side). A staff "grant" button here would be a lawful-basis shortcut, so
 *  there is deliberately none. Withdrawal can only ever REMOVE a permission, which is
 *  why it is safe to expose and a KVKK right to support. */
export function useWithdrawConsent(partyId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (consentId: string) => api.post(`/v1/consents/${consentId}/withdraw`),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["party", partyId, "consents"] });
    },
  });
}

export function useConsents(partyId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ["party", partyId, "consents"],
    queryFn: () => api.get<{ consents: Consent[] }>(`/v1/parties/${partyId}/consents`),
  });
}

// Cross-link counts — the hub. Typed loosely (counts + navigation only; the
// target surfaces own the detail).
function useCount(id: string, panel: string, path: string) {
  const api = useApi();
  return useQuery({ queryKey: hh(id, panel), queryFn: () => api.get<unknown[]>(path) });
}
export const useHouseholdClaims = (id: string) =>
  useCount(id, "claims", `/v1/households/${id}/claims`);
export const useHouseholdQueue = (id: string) =>
  useCount(id, "queue", `/v1/households/${id}/queue`);

/** A household recommendation summary (U3). The read is `GET /v1/households/:id/
 *  recommendations` (recommendation-read.service — a full-row select ordered newest-first),
 *  so each row carries the `id` the badge needs to deep-link into /recommendations/:id. */
export interface HouseholdRec {
  id: string;
  status?: string;
  lineCode?: string;
  createdAt?: string;
}

/** U3 — the household's own recommendations (carries ids, so the "Öneriler (N)" badge can
 *  link to the actual recommendation detail instead of the generic pending-responses list. */
export function useHouseholdRecommendations(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "recommendations"),
    queryFn: () => api.get<HouseholdRec[]>(`/v1/households/${id}/recommendations`),
  });
}

// ── CONSOLE-WIRE-3 — the wired panels + ownership/life-event writes + My Book ──

/** GET …/opportunities (N-adjacency). */
export function useOpportunities(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "opportunities"),
    queryFn: () => api.get<Opportunity[]>(`/v1/households/${id}/opportunities`),
  });
}
/** GET …/renewal-schedule. */
export function useRenewalSchedule(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "renewal-schedule"),
    queryFn: () => api.get<RenewalScheduleItem[]>(`/v1/households/${id}/renewal-schedule`),
  });
}
/** GET …/commission (household ledger). */
export function useHouseholdCommission(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "commission"),
    queryFn: () => api.get<HouseholdCommission>(`/v1/households/${id}/commission`),
  });
}
/** GET …/engagement — the 30-day cross-sell touch count + last contact (summary). */
export function useEngagement(id: string) {
  const api = useApi();
  return useQuery({
    queryKey: hh(id, "engagement"),
    queryFn: () => api.get<Engagement>(`/v1/households/${id}/engagement`),
  });
}

/** POST /v1/life-events — record a life event (sensitivity/CARE is server-derived). */
export function useAddLifeEvent(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateLifeEventInput) => api.post("/v1/life-events", input),
    onSettled: () => qc.invalidateQueries({ queryKey: hh(id, "life-events") }),
  });
}
/** PATCH /v1/life-events/:eventId — mark ACTIONED (the only mutable field). */
export function useUpdateLifeEvent(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { eventId: string; status: "ACTIONED" | "CAPTURED" | "EXPIRED" }) =>
      api.patch(`/v1/life-events/${args.eventId}`, { status: args.status }),
    onSettled: () => qc.invalidateQueries({ queryKey: hh(id, "life-events") }),
  });
}

/** POST …/reassign-owner — household:reassign@all (admin); refreshes ownership. */
export function useReassignOwner(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReassignOwnerInput) =>
      api.post(`/v1/households/${id}/reassign-owner`, input),
    onSettled: () => qc.invalidateQueries({ queryKey: hh(id, "ownership") }),
  });
}
/** POST …/participation — any authenticated staff (self-recorded; no grant gate). */
export function useAddParticipation(id: string) {
  const api = useApi();
  return useMutation({
    mutationFn: (input: ParticipationInput) =>
      api.post(`/v1/households/${id}/participation`, input),
  });
}

/** GET /v1/advisors/me/book — the advisor's household book (My Book surface). */
export function useMyBook() {
  const api = useApi();
  return useQuery({
    queryKey: ["book"],
    queryFn: () => api.get<BookHousehold[]>("/v1/advisors/me/book"),
  });
}

/** GET /v1/advisors/me/book/summary?filter= — the Müşterilerim default view: the advisor's
 *  OWN book (scope-own, masked) enriched with the per-card derived signals + a count. The
 *  `filter` (mine | action-pending | care-hold) narrows the book SERVER-SIDE — switching a
 *  tab refetches (the filter is part of the queryKey). No retry — an empty book is a state,
 *  not a transient. Masked-only: `phone` is the server's masked value (never full PII). */
export function useBookSummary(filter: BookFilter) {
  const api = useApi();
  return useQuery({
    queryKey: ["book-summary", filter],
    retry: false,
    queryFn: () =>
      api.get<BookSummaryView>(`/v1/advisors/me/book/summary?filter=${encodeURIComponent(filter)}`),
  });
}

/** Log an interaction (the one in-scope action). Relationship intents need no
 *  consent; a marketing intent surfaces the API's consent result (the response,
 *  or a problem when an unconsented marketing contact is refused). */
export function useLogInteraction(id: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LogInteractionInput) =>
      api.post<{ interactionId: string }>("/v1/interactions", input),
    onSettled: () => qc.invalidateQueries({ queryKey: hh(id, "interactions") }),
  });
}
